import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { ArrowRight, Calendar, Clock } from 'lucide-react';

export const BlogListPage = () => {
  const blogPosts = [
    {
      id: 1,
      slug: 'gap-analysis-saves-dossier-rejections',
      title: 'How GAP Analysis Saves You from Dossier Rejections',
      excerpt: 'Dossier rejections rarely happen because a product is "bad." They happen because the submission wasn\'t complete, consistent, or aligned with the Ministry of Health\'s expectations.',
      category: 'Regulatory Strategy',
      color: '#606090',
      date: 'Jan 2025',
      readTime: '8 min read',
      image: '/api/placeholder/800/400'
    },
    {
      id: 2,
      slug: 'choose-right-export-markets',
      title: 'How to Choose the Right Export Markets for Your Pharma/Nutra Products',
      excerpt: 'Choosing the right export market is one of the most important strategic decisions manufacturers and exporters make. Learn the data-driven approach to market selection.',
      category: 'Market Strategy',
      color: '#7090A0',
      date: 'Jan 2025',
      readTime: '10 min read',
      image: '/api/placeholder/800/400'
    },
    {
      id: 3,
      slug: 'reduce-time-to-market-exports',
      title: 'How to Reduce Time-to-Market for Export Launches',
      excerpt: 'For pharmaceutical and nutraceutical exporters, getting a product approved quickly can be the difference between leading the market… or entering too late.',
      category: 'Export Strategy',
      color: '#A060B0',
      date: 'Jan 2025',
      readTime: '6 min read',
      image: '/api/placeholder/800/400'
    },
    {
      id: 4,
      slug: 'pharma-artwork-compliance-checklist',
      title: 'Pharma Artwork Compliance: The Exact Checklist You Should Follow',
      excerpt: 'Artwork is one of the most common reasons pharma and nutraceutical dossiers get delayed or rejected. A small mistake can trigger avoidable queries.',
      category: 'Compliance',
      color: '#708080',
      date: 'Jan 2025',
      readTime: '5 min read',
      image: '/api/placeholder/800/400'
    },
    {
      id: 5,
      slug: 'country-specific-branding-product-name',
      title: 'Why Country-Specific Branding Matters: Choosing the Right Product Name',
      excerpt: 'Learn why choosing the right product name is a compliance decision that affects regulatory approval and market success in pharma and nutra exports.',
      category: 'Branding & Compliance',
      color: '#606090',
      date: 'Jan 2025',
      readTime: '7 min read',
      image: '/api/placeholder/800/400'
    },
    {
      id: 6,
      slug: 'smes-avoid-trial-and-error-regulatory',
      title: 'Why SMEs Should Avoid Trial-and-Error Regulatory Approaches',
      excerpt: 'Discover why structured regulatory planning is essential for SME success and how to avoid costly trial-and-error mistakes in pharma exports.',
      category: 'SME Strategy',
      color: '#7090A0',
      date: 'Jan 2025',
      readTime: '8 min read',
      image: '/api/placeholder/800/400'
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F0E0D0' }}>
      <Helmet>
        <title>Blog | TheApostrophe - Regulatory Insights & Export Strategies</title>
        <meta name="description" content="Expert insights on pharma and nutra regulatory compliance, market entry strategies, and export best practices." />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-24 px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6" style={{ color: '#303040' }}>
            From Our Regulatory Desk
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto" style={{ color: '#708080' }}>
            Practical insights, compliance guides, and export strategies to help you launch successfully across emerging markets.
          </p>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-12 px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {blogPosts.map((post) => (
              <Link key={post.id} to={`/blog/${post.slug}`}>
                <Card className="group cursor-pointer transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl border-0 overflow-hidden h-full">
                  <div className="relative h-64 overflow-hidden">
                    <div 
                      className="absolute inset-0 transition-transform duration-300 group-hover:scale-110"
                      style={{ background: `linear-gradient(135deg, ${post.color}30 0%, ${post.color}10 100%)` }}
                    />
                    <div className="absolute bottom-0 left-0 right-0 p-6" style={{ background: 'linear-gradient(to top, rgba(48,48,64,0.9), transparent)' }}>
                      <span 
                        className="inline-block px-3 py-1 rounded-full text-sm font-semibold text-white mb-2"
                        style={{ backgroundColor: post.color }}
                      >
                        {post.category}
                      </span>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-2xl font-bold mb-3 group-hover:text-opacity-80 transition-colors" style={{ color: '#303040' }}>
                      {post.title}
                    </h3>
                    <p className="mb-4 line-clamp-3" style={{ color: '#708080' }}>
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-sm" style={{ color: '#708080' }}>
                      <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {post.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {post.readTime}
                        </span>
                      </div>
                      <span className="font-semibold flex items-center gap-1 group-hover:gap-2 transition-all" style={{ color: post.color }}>
                        Read More
                        <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 overflow-hidden">
        <div className="max-w-4xl mx-auto">
          <Card className="border-0 shadow-2xl rounded-3xl p-12 text-center" style={{ backgroundColor: '#303040' }}>
            <h2 className="text-4xl font-bold mb-6 text-white">
              Need Expert Regulatory Guidance?
            </h2>
            <p className="text-xl mb-8 text-white/80">
              Book a consultation to discuss your specific export challenges and regulatory requirements.
            </p>
            <Link to="/contact">
              <Button
                size="lg"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ backgroundColor: '#A060B0', color: 'white' }}
              >
                Book a Strategy Call
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </Card>
        </div>
      </section>
    </div>
  );
};